<script lang="ts">
  import type { Models } from '@smartlinkdev/types';
  import {onDestroy, onMount, tick} from 'svelte';
  import { goto } from '$app/navigation';
  import FloorChanger from './FloorChanger.svelte';
  import {
    amenities,
    configLoaded,
    locations,
    mapInstance,
    mapReady,
    venue,
    hideControls,
    forceRoveMap
  } from '$lib/stores';
  import { MapEvents, type MapServiceConstructorConfig } from '$lib/types';
  import type { MapService } from '$lib/services/map';
  import RoveFloorChanger from '$lib/components/RoveFloorChanger.svelte';

  interface GetInstancer {
    getInstance(config: MapServiceConstructorConfig): MapService;
  }

  let MapService: GetInstancer;
  let map: MapService;
  let mapType: string;
  let observer: MutationObserver;
  let skyosMap: HTMLElement;

  const initMap = async () => {
    const config: MapServiceConstructorConfig = {
      browserSettings: {
        disableClickRouting: true,
        disableYouAreHere: true,
        directionalYouAreHere: false,
        snapToLine: false,
        deviceRouting: false,
        disableInitialFlyin: false,
        isMobile: false,
      },
      containerId: 'SkyOSMapContainer',
      debug: false,
      settings: {
        venue: $venue,
        locations: $locations,
        amenities: $amenities,
        useRoutingLibrary: true,
      },
    };


    const urlParams = new URLSearchParams(window.location.search);
    const zoom = urlParams.get('zoom');
    if (zoom) {
      config.settings.venue.settings.mapboxSettings.defaultZoom = zoom;
    }
    const bearing = urlParams.get('bearing');
    if (bearing) {
      config.settings.venue.settings.mapboxSettings.defaultBearing = bearing;
    }
    const pitch = urlParams.get('pitch');
    if (pitch) {
      config.settings.venue.settings.mapboxSettings.defaultPitch = pitch;
    }
    const hidecontrols = urlParams.get('hidecontrols');
    if (hidecontrols) {
      hideControls.set(true);
    }

    map = MapService.getInstance(config);
    mapInstance.set(map);
    map.on(MapEvents.LOCATION_CLICK, (location: Models.Location): void => {
      goto(`/venues/${$venue.id}/locations/${location.id}`);
    });

    map.on(MapEvents.ERROR, (err: Error): void => {
      console.log('map err:', err);
    });

    map.on(MapEvents.READY, () => {
      mapReady.set(true);
    });
  };

  const updatePinSize = () => {
      const amenities = document.querySelectorAll('.amenityMarker, .amentity-custom-pin');
      amenities.forEach((el) => {
          el.style.width = '3vw';
          el.style.height = '3vw';
      })
  }

  onMount(async () => {
      await tick();
      if ($venue.settings.mapType === 'rove' || $forceRoveMap) {
          mapType = 'rove';
          // eslint-disable-next-line no-import-assign
          MapService = (await import('../services/roveMap')).MapService;
      } else {
          mapType = 'mapbox';
          // eslint-disable-next-line no-import-assign
          MapService = (await import('../services/map')).MapService;
          observer = new MutationObserver((mut) => {
              mut.forEach((mutation) => {
                  if (mutation.type === 'childList') {
                      if (
                          Array.from(mutation.addedNodes).some((node: Node) => node.classList?.contains("amenityMarker")) ||
                          Array.from(mutation.removedNodes).some((node: Node) => node.classList?.contains("amenityMarker"))
                      ) {
                          updatePinSize();
                      }
                  }
              });
          });
          observer.observe(skyosMap, {childList: true, subtree: true});
      }
  });

  onDestroy(() => {
      observer.disconnect();
  });

  $: if ($configLoaded && venue && MapService) {
    initMap();
  }
</script>

{#if configLoaded}
  <div id="SkyOSMapContainer" bind:this={skyosMap}/>
  {#if !$hideControls}
    {#if mapType === 'rove'}
      <div class="map-branding">
        <img
          class="logo-img rovemaps-logo"
          src="/rovemaps.svg"
          alt="Rove Maps Logo"
        />
      </div>
      <RoveFloorChanger />
    {:else}
      <div class="map-branding">
        <img
          class="logo-img"
          src="/rove-iq-logo.svg"
          alt="RoveIQ logo"
        />
      </div>
      <FloorChanger />
    {/if}
  {/if}
{/if}

<style lang="postcss">
  #SkyOSMapContainer {
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
  }

  .map-branding {
    @apply w-[100vw] absolute top-[10px];
  }
  .logo-img {
    @apply absolute h-[25px] right-[20px] md:right-[20px] lg:right-[470px] xl:right-[500px];
  }

  .rovemaps-logo{
    height: 35px;
  }

</style>
